
import { Question } from "../../types/assessment";
import { createGenericOptions } from "../utils";

export const talentQuestions: Question[] = [
  {
    id: "t1",
    text: "How many AI specialists (data scientists, ML engineers) are on staff?",
    dimension: "talent",
    tooltip: "Count the number of full-time employees with specialized AI expertise such as data scientists, machine learning engineers, AI researchers, and related roles focused primarily on AI development.",
    options: [
      {
        value: 0,
        label: "No dedicated AI specialists or data science team members",
        description: "Organization has no employees specifically hired for AI-related roles. Any AI work is handled by general IT staff without specialized expertise."
      },
      {
        value: 1,
        label: "Small team (1-2 specialists) focusing on basic ML implementations",
        description: "Minimal in-house expertise limited to 1-2 specialists. Focus is on implementing simple machine learning models, often using pre-built solutions."
      },
      {
        value: 2,
        label: "Growing team (3-5 specialists) with mixed expertise levels in ML/AI",
        description: "Small team with varied experience levels. Capability to implement standard ML algorithms and customize solutions for specific business needs."
      },
      {
        value: 3,
        label: "Established team (6-10 specialists) covering multiple AI disciplines",
        description: "Moderate-sized team covering key AI specializations. Includes data scientists, ML engineers, and data engineers with complementary skills."
      },
      {
        value: 4,
        label: "Advanced team (11-20 specialists) with specialized expertise areas",
        description: "Substantial team with dedicated specialists across multiple AI domains. Includes deep learning experts, NLP specialists, computer vision engineers, and AI researchers."
      },
      {
        value: 5,
        label: "Enterprise-scale team (20+ specialists) with comprehensive AI capabilities",
        description: "Large specialized team covering all relevant AI disciplines. Includes PhD-level researchers, domain experts, specialized engineers, and AI ethicists working collaboratively."
      }
    ]
  },
  {
    id: "t2",
    text: "What AI training programs are available for employees?",
    dimension: "talent",
    tooltip: "Evaluate the scope, depth, and availability of AI education opportunities across your organization. Consider formal training, workshops, certification programs, and self-guided learning resources.",
    options: [
      {
        value: 0,
        label: "No formal AI training or educational resources available",
        description: "No structured programs or resources for AI education. Employees must pursue any AI learning completely independently without organizational support."
      },
      {
        value: 1,
        label: "Basic AI awareness sessions (1-2 hours monthly) with introductory content",
        description: "Minimal training limited to awareness sessions introducing AI concepts. No hands-on components or structured learning paths for skill development."
      },
      {
        value: 2,
        label: "Regular workshops (8-16 hours quarterly) with hands-on exercises",
        description: "Quarterly workshops provide basic practical experience. Focus on fundamental concepts with simple exercises but limited advanced content."
      },
      {
        value: 3,
        label: "Comprehensive programs (40+ hours) with certification paths",
        description: "Structured learning programs with substantial content and certification options. Includes both theoretical knowledge and practical application exercises."
      },
      {
        value: 4,
        label: "Advanced learning tracks (100+ hours) with specialization options",
        description: "Extensive training curriculum offering multiple specialization paths. Combines formal instruction, hands-on projects, and recognized certifications."
      },
      {
        value: 5,
        label: "Full-scale AI academy with customized learning paths and mentorship",
        description: "Enterprise AI academy providing comprehensive education from fundamentals to advanced topics. Includes personalized learning paths, mentorship, practical projects, and academic partnerships."
      }
    ]
  },
  {
    id: "t3",
    text: "How do you assess AI skills in your organization?",
    dimension: "talent",
    tooltip: "Evaluate your organization's process for identifying, measuring, and tracking AI-related skills and competencies. Consider both technical skills assessment and business understanding of AI applications.",
    options: [
      {
        value: 0,
        label: "No formal AI skills assessment or tracking system",
        description: "No process exists to identify or measure AI capabilities. Skills are neither systematically identified nor tracked across the organization."
      },
      {
        value: 1,
        label: "Basic competency checklist with annual reviews",
        description: "Simple self-reported skills inventory conducted during annual reviews. Lacks verification, standardization, or comprehensive skill mapping."
      },
      {
        value: 2,
        label: "Structured assessment framework with quarterly evaluations",
        description: "Regular skills assessment using standardized frameworks. Evaluations are primarily subjective with limited validation of actual capabilities."
      },
      {
        value: 3,
        label: "Comprehensive skills matrix with regular gap analysis",
        description: "Detailed skills mapping with regular gap analysis against requirements. Combines self-assessment, manager evaluation, and objective skill demonstrations."
      },
      {
        value: 4,
        label: "Advanced competency tracking with personalized development plans",
        description: "Sophisticated skills assessment integrated with individual development plans. Includes quantifiable metrics, peer reviews, and project-based evaluations."
      },
      {
        value: 5,
        label: "Dynamic skill assessment platform with real-time progress monitoring",
        description: "State-of-the-art skill assessment system with continuous monitoring. Combines project metrics, certifications, peer reviews, and adaptive testing with predictive analytics."
      }
    ]
  },
  {
    id: "t4",
    text: "What is your AI talent retention rate?",
    dimension: "talent",
    tooltip: "Measure the annual percentage of AI specialists who remain with your organization. Consider factors like competitive compensation, career growth opportunities, and work environment that influence retention.",
    options: [
      {
        value: 0,
        label: "High turnover (>40% annually) with significant knowledge loss",
        description: "Very poor retention with nearly half of AI talent leaving annually. Significant knowledge loss and disruption to projects with minimal continuity."
      },
      {
        value: 1,
        label: "Above average turnover (30-40%) with limited retention strategies",
        description: "Well above industry average turnover rates. Basic retention efforts exist but fail to address core issues causing departures."
      },
      {
        value: 2,
        label: "Moderate turnover (20-30%) with basic retention programs",
        description: "Turnover somewhat higher than industry average. Standard retention approaches like competitive pay and basic recognition programs in place."
      },
      {
        value: 3,
        label: "Good retention (10-20%) with structured career paths",
        description: "Turnover at or below industry average. Established career development paths and recognition programs support employee retention."
      },
      {
        value: 4,
        label: "Strong retention (5-10%) with competitive benefits and growth",
        description: "Excellent retention rates significantly below industry average. Comprehensive retention strategy includes competitive compensation, growth opportunities, and work-life balance."
      },
      {
        value: 5,
        label: "Exceptional retention (<5%) with industry-leading development opportunities",
        description: "Best-in-class retention with minimal voluntary departures. Renowned for exceptional work environment, cutting-edge projects, industry-leading compensation, and substantial growth opportunities."
      }
    ]
  },
  {
    id: "t5",
    text: "How do you develop AI leadership capabilities?",
    dimension: "talent",
    tooltip: "Assess your organization's approach to developing leaders who understand AI technologies and can effectively guide AI initiatives. Consider both technical knowledge development and leadership skill building.",
    options: [
      {
        value: 0,
        label: "No AI leadership development programs exist",
        description: "No initiatives specifically target AI leadership development. Leaders must independently acquire relevant knowledge without organizational support."
      },
      {
        value: 1,
        label: "Basic management training with AI awareness components",
        description: "Standard leadership training includes minimal AI awareness material. Focuses on conceptual understanding rather than applied leadership of AI initiatives."
      },
      {
        value: 2,
        label: "Structured AI leadership program with quarterly workshops",
        description: "Regular leadership workshops address AI concepts and basic implementation challenges. Limited practical application with primarily theoretical content."
      },
      {
        value: 3,
        label: "Comprehensive development track with mentorship program",
        description: "Dedicated AI leadership curriculum combines technical and managerial components. Mentorship from experienced AI leaders provides practical guidance and support."
      },
      {
        value: 4,
        label: "Advanced leadership academy with external partnerships",
        description: "Sophisticated leadership development program includes external education partners. Combines intensive technical training with strategic leadership development."
      },
      {
        value: 5,
        label: "Executive AI development program with global exposure",
        description: "Premier leadership program includes global exposure to leading AI organizations. Combines advanced technical understanding with transformational leadership capabilities."
      }
    ]
  },
  {
    id: "t6",
    text: "What collaboration exists between AI teams and business units?",
    dimension: "talent",
    tooltip: "Evaluate how effectively AI specialists work with business teams to identify needs, develop solutions, and implement AI projects. Consider both formal collaboration structures and communication processes.",
    options: [
      {
        value: 0,
        label: "No structured collaboration between AI teams and business units",
        description: "Complete disconnect between technical and business teams. AI initiatives proceed without business input or alignment with operational requirements."
      },
      {
        value: 1,
        label: "Limited interaction (20% projects) with basic knowledge sharing",
        description: "Minimal collaboration with business teams occurs in a small fraction of projects. Communication is primarily one-directional with little interactive problem-solving."
      },
      {
        value: 2,
        label: "Regular collaboration (40% projects) with defined processes",
        description: "Moderate level of collaboration with basic processes for requirements gathering. Business teams provide input but remain somewhat separated from development."
      },
      {
        value: 3,
        label: "Active partnership (60% projects) with shared objectives",
        description: "Majority of projects involve active business participation. Shared objectives and regular communication create alignment between technical and business perspectives."
      },
      {
        value: 4,
        label: "Deep integration (80% projects) with cross-functional teams",
        description: "High level of integration with cross-functional teams that include both technical and business roles. Collaborative approach throughout project lifecycle."
      },
      {
        value: 5,
        label: "Full collaboration (95%+ projects) with embedded AI expertise",
        description: "Nearly complete integration where AI specialists are embedded within business units. Boundaries between technical and business teams are minimal with continuous collaboration."
      }
    ]
  },
  {
    id: "t7",
    text: "How do you measure AI team performance?",
    dimension: "talent",
    tooltip: "Assess the maturity of your metrics and processes for evaluating the effectiveness of AI teams. Consider both technical performance indicators and business impact measurements.",
    options: [
      {
        value: 0,
        label: "No formal AI team performance metrics in place",
        description: "No systematic evaluation of AI team effectiveness exists. Performance assessment is subjective without defined metrics or standards."
      },
      {
        value: 1,
        label: "Basic output measures tracked quarterly (project completion)",
        description: "Simple metrics focus on project completion rather than quality or impact. Quarterly reviews track basic output measures and deadlines met."
      },
      {
        value: 2,
        label: "Monthly KPI tracking with technical and delivery metrics",
        description: "Regular monitoring of standard metrics covering technical performance and delivery efficiency. Limited connection to business outcomes."
      },
      {
        value: 3,
        label: "Weekly performance monitoring with balanced scorecard",
        description: "Comprehensive metrics framework balances technical quality, efficiency, and business impact. Regular reviews ensure alignment with organizational goals."
      },
      {
        value: 4,
        label: "Comprehensive metrics including innovation and value creation",
        description: "Sophisticated performance framework includes value creation, innovation metrics, and team development. Combines quantitative measures with qualitative assessment."
      },
      {
        value: 5,
        label: "Advanced analytics with predictive performance indicators",
        description: "Cutting-edge performance measurement uses analytics to predict outcomes and optimize team effectiveness. Real-time dashboards with leading indicators drive continuous improvement."
      }
    ]
  },
  {
    id: "t8",
    text: "What is your AI recruitment strategy?",
    dimension: "talent",
    tooltip: "Evaluate your organization's approach to finding and hiring AI talent. Consider sourcing channels, assessment methods, employer branding, and overall strategy for competing in the AI talent market.",
    options: [
      {
        value: 0,
        label: "No specific AI talent recruitment strategy",
        description: "No specialized approach for AI talent acquisition. Relies on general recruitment methods without targeting specialized skills or communities."
      },
      {
        value: 1,
        label: "Basic job postings on general platforms (2-3 channels)",
        description: "Simple recruitment approach using mainstream job boards. Limited targeting with generic job descriptions and basic screening processes."
      },
      {
        value: 2,
        label: "Targeted recruitment through specialized channels (4-5)",
        description: "More focused approach using technical job platforms and specialized recruiters. Improved job descriptions with standard technical assessments."
      },
      {
        value: 3,
        label: "Multi-channel strategy with talent community (6-8)",
        description: "Comprehensive recruitment across multiple channels with an established talent community. Technical assessments combined with cultural fit evaluation."
      },
      {
        value: 4,
        label: "Advanced talent pipeline with university partnerships",
        description: "Sophisticated talent acquisition strategy with proactive pipeline development. Includes academic partnerships, internship programs, and specialized recruitment events."
      },
      {
        value: 5,
        label: "Comprehensive talent strategy with global reach (10+ channels)",
        description: "Industry-leading recruitment approach combining global sourcing, prestigious partnerships, talent incubation programs, and recognized employer brand within AI community."
      }
    ]
  },
  {
    id: "t9",
    text: "How do you manage AI knowledge transfer?",
    dimension: "talent",
    tooltip: "Assess your organization's approach to capturing, sharing, and preserving AI knowledge. Consider documentation practices, communities of practice, and knowledge management systems.",
    options: [
      {
        value: 0,
        label: "No formal knowledge management system for AI",
        description: "No structured approach to knowledge sharing or retention. Expertise remains siloed within individuals with high risk of knowledge loss."
      },
      {
        value: 1,
        label: "Basic documentation covering 25% of critical processes",
        description: "Limited documentation exists for some key processes. Ad hoc knowledge sharing occurs but without systematic capture or dissemination."
      },
      {
        value: 2,
        label: "Structured documentation (50%) with standard templates",
        description: "Half of important AI knowledge is documented using standardized approaches. Regular team meetings facilitate some knowledge exchange."
      },
      {
        value: 3,
        label: "Comprehensive system (75%) with version control",
        description: "Majority of AI processes and knowledge are documented with proper version control. Communities of practice enable cross-team knowledge sharing."
      },
      {
        value: 4,
        label: "Advanced platform (90%) with search and collaboration",
        description: "Nearly all AI knowledge captured in sophisticated knowledge management system. Includes powerful search, collaboration tools, and multimedia content."
      },
      {
        value: 5,
        label: "Enterprise knowledge system with AI-powered recommendations",
        description: "State-of-the-art knowledge platform uses AI to connect experts, surface relevant content, and recommend resources. Seamless capture of tacit knowledge with intelligent retrieval."
      }
    ]
  },
  {
    id: "t10",
    text: "Does your organization offer incentives for employees to innovate with AI?",
    dimension: "talent",
    tooltip: "Evaluate the range of incentives - both financial and non-financial - offered to encourage employees to develop innovative AI applications. Consider recognition programs, monetary rewards, and career advancement opportunities.",
    options: [
      {
        value: 0,
        label: "No AI innovation incentives or recognition program",
        description: "No specific incentives exist to encourage AI innovation. Standard performance evaluation applies without special consideration for AI contributions."
      },
      {
        value: 1,
        label: "Basic recognition program without monetary rewards",
        description: "Simple acknowledgment programs recognize AI contributions. Limited to informal recognition without substantial rewards or advancement opportunities."
      },
      {
        value: 2,
        label: "Annual innovation awards ($1000-5000 per project)",
        description: "Yearly awards program provides modest financial incentives for noteworthy AI innovations. Recognition is primarily ceremonial with limited career impact."
      },
      {
        value: 3,
        label: "Quarterly innovation fund ($5000-20000 available)",
        description: "Regular funding cycles support promising AI innovations with moderate financial rewards. Successful projects gain visibility with potential career advancement."
      },
      {
        value: 4,
        label: "Dedicated innovation program ($20000-50000 budget)",
        description: "Substantial innovation program combines significant financial rewards with career advancement. Includes dedicated time allocation and mentorship for promising ideas."
      },
      {
        value: 5,
        label: "Comprehensive innovation system ($50000+ with equity options)",
        description: "Premium innovation ecosystem offers substantial rewards, equity participation, and accelerated career paths. Includes incubation resources, executive exposure, and commercialization support."
      }
    ]
  }
];
